/**
 * ShiprocketSettings.js
 * Client Portal - Shiprocket Shipping Integration Settings
 * Allows masters (clients) to configure:
 * - Enable/disable Shiprocket shipping
 * - Fetch & view pickup addresses (read-only — manage on Shiprocket portal)
 * - Map pickup addresses to branches
 * - Multi-select preferred couriers with weight-based rules
 * - Set default package dimensions & weight
 * - Channel selection, webhook token
 * - Automation toggles (auto-create, AWB, pickup, label, manifest)
 */

import React, { useState, useEffect, useCallback } from "react";
import {
    FaRocket, FaMapMarkerAlt, FaBox, FaCog, FaCheck, FaTimes, FaSave, FaSync,
    FaTruck, FaWeightHanging, FaPlus, FaTrash, FaLink, FaExternalLinkAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
    getShiprocketSettings,
    updateShiprocketSettings,
    getShiprocketPickupAddresses,
    getShiprocketChannels,
    getShiprocketCourierRates,
    fetchBranchesData,
} from "../../services/ClientService";

const ShiprocketSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState("general");

    // Settings state
    const [settings, setSettings] = useState({
        enabled: false,
        channelId: "",
        branchPickupMappings: [],
        defaultPackageDimensions: { length: 10, breadth: 10, height: 10 },
        defaultWeight: 0.5,
        preferredCourierMode: "cheapest", // cheapest, fastest, custom
        preferredCouriers: [], // [{courier_company_id, courier_name}]
        courierRules: {
            weightSlabs: [], // [{maxWeightKg, preferredMode, preferredCouriers:[]}]
            codPreference: "cheapest",
        },
        autoCreateShipment: true,
        autoAssignAWB: true,
        autoSchedulePickup: false,
        autoGenerateLabel: true,
        autoGenerateManifest: false,
        webhookToken: "",
        hyperlocalCategory: "Others",
    });

    // Pickup addresses from Shiprocket (read-only)
    const [pickupAddresses, setPickupAddresses] = useState([]);
    const [loadingPickupAddresses, setLoadingPickupAddresses] = useState(false);

    // Channels from Shiprocket
    const [channels, setChannels] = useState([]);
    const [loadingChannels, setLoadingChannels] = useState(false);

    // Branches
    const [branches, setBranches] = useState([]);

    // Available couriers (fetched from Shiprocket serviceability check)
    const [availableCouriers, setAvailableCouriers] = useState([]);
    const [loadingCouriers, setLoadingCouriers] = useState(false);
    const [courierTestPincodes, setCourierTestPincodes] = useState({
        pickup: "",
        delivery: "",
    });
    const [courierSearch, setCourierSearch] = useState("");
    const [courierModeFilter, setCourierModeFilter] = useState("All");

    // Fetch initial data
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, branchesRes] = await Promise.all([
                getShiprocketSettings(),
                fetchBranchesData(),
            ]);

            if (settingsRes?.success && settingsRes.data) {
                setSettings(prev => ({
                    ...prev,
                    ...settingsRes.data,
                    courierRules: {
                        ...prev.courierRules,
                        ...(settingsRes.data.courierRules || {}),
                    },
                    defaultPackageDimensions: {
                        ...prev.defaultPackageDimensions,
                        ...(settingsRes.data.defaultPackageDimensions || {}),
                    },
                }));
            }

            if (branchesRes?.success) {
                setBranches(branchesRes.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch settings:", error);
            toast.error("Failed to load settings");
        } finally {
            setLoading(false);
        }
    };

    const fetchPickupAddresses = useCallback(async () => {
        try {
            setLoadingPickupAddresses(true);
            const res = await getShiprocketPickupAddresses();
            if (res?.success) {
                setPickupAddresses(res.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch pickup addresses:", error);
            toast.error("Failed to load pickup addresses");
        } finally {
            setLoadingPickupAddresses(false);
        }
    }, []);

    const fetchChannels = useCallback(async () => {
        try {
            setLoadingChannels(true);
            const res = await getShiprocketChannels();
            if (res?.success) {
                setChannels(res.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch channels:", error);
        } finally {
            setLoadingChannels(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === "pickup" || activeTab === "branches") {
            fetchPickupAddresses();
        }
        if (activeTab === "general") {
            fetchChannels();
        }
    }, [activeTab, fetchPickupAddresses, fetchChannels]);

    // Save settings
    const handleSave = async () => {
        try {
            setSaving(true);
            const res = await updateShiprocketSettings(settings);
            if (res?.success) {
                toast.success("Shiprocket settings saved successfully!");
            } else {
                toast.error(res?.message || "Failed to save settings");
            }
        } catch (error) {
            console.error("Save failed:", error);
            toast.error("Failed to save settings");
        } finally {
            setSaving(false);
        }
    };

    // Fetch available couriers for selection via serviceability check
    const handleFetchCouriers = async () => {
        if (!courierTestPincodes.pickup || !courierTestPincodes.delivery) {
            toast.error("Enter both pickup and delivery pincodes to fetch available couriers");
            return;
        }
        try {
            setLoadingCouriers(true);
            const res = await getShiprocketCourierRates({
                pickupPincode: courierTestPincodes.pickup,
                deliveryPincode: courierTestPincodes.delivery,
                weight: settings.defaultWeight || 0.5,
                cod: 0,
            });
            if (res?.success && res.data) {
                const couriers = res.data?.couriers || res.data?.data?.available_courier_companies || res.data?.available_courier_companies || [];
                setAvailableCouriers(couriers);
                if (couriers.length === 0) {
                    toast.info("No couriers available for this route. Try different pincodes.");
                }
            }
        } catch (error) {
            console.error("Failed to fetch couriers:", error);
            toast.error("Failed to fetch courier list");
        } finally {
            setLoadingCouriers(false);
        }
    };

    // Toggle a courier in preferred list
    const togglePreferredCourier = (courierId, courierName) => {
        setSettings(prev => {
            const existing = prev.preferredCouriers || [];
            const isSelected = existing.some(c => c.courier_company_id === courierId);
            if (isSelected) {
                return { ...prev, preferredCouriers: existing.filter(c => c.courier_company_id !== courierId) };
            } else {
                return { ...prev, preferredCouriers: [...existing, { courier_company_id: courierId, courier_name: courierName }] };
            }
        });
    };

    // Weight slab management
    const addWeightSlab = () => {
        setSettings(prev => ({
            ...prev,
            courierRules: {
                ...prev.courierRules,
                weightSlabs: [
                    ...(prev.courierRules?.weightSlabs || []),
                    { maxWeightKg: 1, preferredMode: "cheapest", preferredCouriers: [] },
                ],
            },
        }));
    };

    const updateWeightSlab = (index, field, value) => {
        setSettings(prev => {
            const slabs = [...(prev.courierRules?.weightSlabs || [])];
            slabs[index] = { ...slabs[index], [field]: value };
            return { ...prev, courierRules: { ...prev.courierRules, weightSlabs: slabs } };
        });
    };

    const removeWeightSlab = (index) => {
        setSettings(prev => {
            const slabs = [...(prev.courierRules?.weightSlabs || [])];
            slabs.splice(index, 1);
            return { ...prev, courierRules: { ...prev.courierRules, weightSlabs: slabs } };
        });
    };

    const toggleSlabCourier = (slabIndex, courierId, courierName) => {
        setSettings(prev => {
            const slabs = [...(prev.courierRules?.weightSlabs || [])];
            const slab = { ...slabs[slabIndex] };
            const existing = slab.preferredCouriers || [];
            const isSelected = existing.some(c => c.courier_company_id === courierId);
            slab.preferredCouriers = isSelected
                ? existing.filter(c => c.courier_company_id !== courierId)
                : [...existing, { courier_company_id: courierId, courier_name: courierName }];
            slabs[slabIndex] = slab;
            return { ...prev, courierRules: { ...prev.courierRules, weightSlabs: slabs } };
        });
    };

    // Update branch mapping (includes pincode from Shiprocket pickup address)
    const handleBranchMappingChange = (branchId, branchName, pickupLocationName, pincode) => {
        setSettings(prev => {
            const mappings = [...(prev.branchPickupMappings || [])];
            const existingIndex = mappings.findIndex(m => m.branchId === branchId);

            if (existingIndex >= 0) {
                if (pickupLocationName) {
                    mappings[existingIndex] = { branchId, branchName, pickupLocationName, pincode: pincode || '' };
                } else {
                    mappings.splice(existingIndex, 1);
                }
            } else if (pickupLocationName) {
                mappings.push({ branchId, branchName, pickupLocationName, pincode: pincode || '' });
            }

            return { ...prev, branchPickupMappings: mappings };
        });
    };

    const tabs = [
        { key: "general", label: "General", icon: <FaCog /> },
        { key: "couriers", label: "Courier Preferences", icon: <FaTruck /> },
        { key: "pickup", label: "Pickup Addresses", icon: <FaMapMarkerAlt /> },
        { key: "branches", label: "Branch Mapping", icon: <FaBox /> },
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 p-3 rounded-xl">
                        <FaRocket className="text-white text-2xl" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Shiprocket Settings</h1>
                        <p className="text-sm text-gray-500">Configure shipping integration for inter-city & inter-state deliveries</p>
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
                >
                    {saving ? <FaSync className="animate-spin" /> : <FaSave />}
                    {saving ? "Saving..." : "Save Settings"}
                </button>
            </div>

            {/* Enable/Disable Toggle */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Enable Shiprocket Shipping</h3>
                        <p className="text-sm text-gray-500 mt-1">
                            When enabled, Shiprocket delivery options will appear alongside your local delivery options.
                            Customers can choose Shiprocket for long-distance or inter-state deliveries.
                        </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={settings.enabled}
                            onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                            className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                </div>
            </div>

            {settings.enabled && (
                <>
                    {/* Tabs */}
                    <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1">
                        {tabs.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center
                                    ${activeTab === tab.key
                                        ? "bg-white text-blue-600 shadow-sm"
                                        : "text-gray-500 hover:text-gray-700"
                                    }`}
                            >
                                {tab.icon}
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* ===== GENERAL SETTINGS TAB ===== */}
                    {activeTab === "general" && (
                        <div className="space-y-6">
                            {/* Channel Selection */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Shiprocket Channel</h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    Select the Shiprocket channel to create orders under. If unsure, leave blank for the default channel.
                                </p>
                                <div className="flex items-center gap-3">
                                    <select
                                        value={settings.channelId || ""}
                                        onChange={(e) => setSettings(prev => ({ ...prev, channelId: e.target.value }))}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">-- Default Channel --</option>
                                        {channels.map((ch) => (
                                            <option key={ch.id} value={ch.id}>
                                                {ch.name} (ID: {ch.id})
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={fetchChannels}
                                        disabled={loadingChannels}
                                        className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                                    >
                                        <FaSync className={loadingChannels ? "animate-spin" : ""} />
                                        Refresh
                                    </button>
                                </div>
                            </div>

                            {/* Automation Toggles */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Automation Settings</h3>
                                <div className="space-y-4">
                                    {[
                                        {
                                            key: "autoCreateShipment",
                                            label: "Auto-Create Shiprocket Shipment",
                                            desc: "Automatically create a shipment on Shiprocket when order status changes to Confirmed",
                                        },
                                        {
                                            key: "autoAssignAWB",
                                            label: "Auto-Assign AWB (Tracking Number)",
                                            desc: "Automatically assign a courier and AWB number after shipment creation",
                                        },
                                        {
                                            key: "autoSchedulePickup",
                                            label: "Auto-Schedule Pickup",
                                            desc: "Automatically schedule courier pickup after AWB assignment",
                                        },
                                        {
                                            key: "autoGenerateLabel",
                                            label: "Auto-Generate Shipping Label",
                                            desc: "Automatically generate the shipping label after AWB is assigned",
                                        },
                                        {
                                            key: "autoGenerateManifest",
                                            label: "Auto-Generate Manifest",
                                            desc: "Automatically generate a manifest after label generation",
                                        },
                                    ].map(toggle => (
                                        <div key={toggle.key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                                            <div>
                                                <h4 className="font-medium text-gray-900">{toggle.label}</h4>
                                                <p className="text-sm text-gray-500">{toggle.desc}</p>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={settings[toggle.key] || false}
                                                    onChange={(e) => setSettings(prev => ({ ...prev, [toggle.key]: e.target.checked }))}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Delivery Pricing Controls */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">Delivery Pricing Controls</h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    Control how delivery charges are displayed to customers in the cart.
                                </p>
                                <div className="space-y-5">
                                    {/* COD Charges Toggle */}
                                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                        <div>
                                            <h4 className="font-medium text-gray-900">Exclude COD Charges from Delivery Price</h4>
                                            <p className="text-sm text-gray-500">
                                                When enabled, delivery charges shown to customers will exclude the COD surcharge — even if the customer selects Cash on Delivery. The displayed rate will be the same as for online/prepaid payments.
                                            </p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
                                            <input
                                                type="checkbox"
                                                checked={settings.excludeCodChargesFromDelivery || false}
                                                onChange={(e) => setSettings(prev => ({ ...prev, excludeCodChargesFromDelivery: e.target.checked }))}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                    {/* Max Delivery Charge Threshold */}
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">Maximum Delivery Charge (₹)</h4>
                                        <p className="text-sm text-gray-500 mb-2">
                                            Set a cap on Shiprocket delivery charges. If the actual courier rate exceeds this amount, the customer will be charged only this threshold price. Set to 0 to disable (no cap).
                                        </p>
                                        <div className="flex items-center gap-3">
                                            <span className="text-gray-400 font-medium">₹</span>
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                value={settings.maxDeliveryCharge || 0}
                                                onChange={(e) => setSettings(prev => ({ ...prev, maxDeliveryCharge: Number(e.target.value) || 0 }))}
                                                className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="0"
                                            />
                                            {settings.maxDeliveryCharge > 0 && (
                                                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                                    Capped at ₹{settings.maxDeliveryCharge}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {/* Hyperlocal Max Delivery Charge Threshold */}
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">Maximum Hyperlocal Delivery Charge (₹)</h4>
                                        <p className="text-sm text-gray-500 mb-2">
                                            Set a separate cap on <strong>Hyperlocal (same-day)</strong> delivery charges. If not set (0), the general Maximum Delivery Charge above will apply to hyperlocal options as well.
                                        </p>
                                        <div className="flex items-center gap-3">
                                            <span className="text-gray-400 font-medium">₹</span>
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                value={settings.maxHyperlocalDeliveryCharge || 0}
                                                onChange={(e) => setSettings(prev => ({ ...prev, maxHyperlocalDeliveryCharge: Number(e.target.value) || 0 }))}
                                                className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="0"
                                            />
                                            {settings.maxHyperlocalDeliveryCharge > 0 && (
                                                <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                                    ⚡ Hyperlocal capped at ₹{settings.maxHyperlocalDeliveryCharge}
                                                </span>
                                            )}
                                            {!settings.maxHyperlocalDeliveryCharge && settings.maxDeliveryCharge > 0 && (
                                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                    Using general cap: ₹{settings.maxDeliveryCharge}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {/* Hyperlocal Shipment Category */}
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">Hyperlocal Shipment Category</h4>
                                        <p className="text-sm text-gray-500 mb-2">
                                            Shiprocket Quick (hyperlocal) requires a product category. Select the category that best matches your products.
                                        </p>
                                        <div className="flex items-center gap-3">
                                            <select
                                                value={settings.hyperlocalCategory || "Others"}
                                                onChange={(e) => setSettings(prev => ({ ...prev, hyperlocalCategory: e.target.value }))}
                                                className="w-56 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                {["Electronics", "Clothes", "Medicines", "Food", "Documents", "Groceries", "Loose Goods", "Others"].map(cat => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                ⚡ Required for Quick Shiprocket orders
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Default Package Dimensions & Weight */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Default Package Dimensions & Weight</h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    Set default dimensions (cm) and weight (kg) for shipment creation. These can be overridden per order.
                                </p>
                                <div className="grid grid-cols-4 gap-4">
                                    {["length", "breadth", "height"].map(dim => (
                                        <div key={dim}>
                                            <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">{dim} (cm)</label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="200"
                                                value={settings.defaultPackageDimensions?.[dim] || 10}
                                                onChange={(e) => setSettings(prev => ({
                                                    ...prev,
                                                    defaultPackageDimensions: {
                                                        ...prev.defaultPackageDimensions,
                                                        [dim]: Number(e.target.value) || 10,
                                                    },
                                                }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                    ))}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
                                        <input
                                            type="number"
                                            min="0.1"
                                            max="50"
                                            step="0.1"
                                            value={settings.defaultWeight || 0.5}
                                            onChange={(e) => setSettings(prev => ({ ...prev, defaultWeight: Number(e.target.value) || 0.5 }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Webhook Token */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Webhook Configuration</h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    Get real-time tracking updates pushed to your server automatically. Configure this URL
                                    in your Shiprocket dashboard under <strong>Settings → Additional Settings → Webhooks</strong>.
                                </p>
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                                    <p className="text-sm text-amber-800">
                                        <strong>⚠️ Important Rules:</strong>
                                    </p>
                                    <ul className="text-xs text-amber-700 mt-1 list-disc list-inside space-y-1">
                                        <li>The URL must be <strong>publicly accessible</strong> (not localhost)</li>
                                        <li>URL must NOT contain keywords: shiprocket, kartrocket, sr, or kr</li>
                                        <li>Endpoint must accept <strong>POST</strong> and return <strong>200 OK</strong></li>
                                        <li>Token is sent as <code>x-api-key</code> header (optional but recommended)</li>
                                    </ul>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL (copy to Shiprocket)</label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                readOnly
                                                value={`${window.location.origin.replace(/:\d+$/, '').replace('client.', 'api.')}/zuntly/tracking/push-updates`}
                                                className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-600"
                                            />
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(
                                                        `${window.location.origin.replace(/:\d+$/, '').replace('client.', 'api.')}/zuntly/tracking/push-updates`
                                                    );
                                                    toast.success("Webhook URL copied!");
                                                }}
                                                className="px-3 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 text-sm"
                                            >
                                                <FaLink />
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Webhook Secret Token (optional — set in Shiprocket "Token" field)
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter a secret token for webhook verification"
                                            value={settings.webhookToken || ""}
                                            onChange={(e) => setSettings(prev => ({ ...prev, webhookToken: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ===== COURIER PREFERENCES TAB ===== */}
                    {activeTab === "couriers" && (
                        <div className="space-y-6">
                            {/* Preferred Courier Mode */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Preferred Courier Selection Mode</h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    Choose how courier partners are selected when assigning AWB to shipments.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {[
                                        { value: "cheapest", label: "Cheapest", desc: "Always select the lowest-cost courier", icon: "💰" },
                                        { value: "fastest", label: "Fastest", desc: "Prioritize quickest delivery ETA", icon: "⚡" },
                                        { value: "custom", label: "Custom", desc: "Only use your hand-picked couriers", icon: "🎯" },
                                    ].map(mode => (
                                        <button
                                            key={mode.value}
                                            onClick={() => setSettings(prev => ({ ...prev, preferredCourierMode: mode.value }))}
                                            className={`p-4 rounded-xl border-2 text-left transition-all
                                                ${settings.preferredCourierMode === mode.value
                                                    ? "border-blue-500 bg-blue-50"
                                                    : "border-gray-200 hover:border-gray-300"
                                                }`}
                                        >
                                            <span className="text-2xl mb-2 block">{mode.icon}</span>
                                            <h4 className="font-semibold text-gray-900">{mode.label}</h4>
                                            <p className="text-xs text-gray-500 mt-1">{mode.desc}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* COD Preference */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">COD Order Preference</h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    How should couriers be selected for Cash-on-Delivery orders?
                                </p>
                                <div className="flex gap-3">
                                    {[
                                        { value: "cheapest", label: "Cheapest" },
                                        { value: "fastest", label: "Fastest" },
                                        { value: "same", label: "Same as above" },
                                    ].map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => setSettings(prev => ({
                                                ...prev,
                                                courierRules: { ...prev.courierRules, codPreference: opt.value },
                                            }))}
                                            className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all
                                                ${(settings.courierRules?.codPreference || "cheapest") === opt.value
                                                    ? "border-blue-500 bg-blue-50 text-blue-700"
                                                    : "border-gray-200 hover:border-gray-300 text-gray-600"
                                                }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Custom Courier Multi-Select (when mode is "custom") */}
                            {settings.preferredCourierMode === "custom" && (
                                <div className="bg-white rounded-xl border border-gray-200 p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Preferred Couriers</h3>
                                    <p className="text-sm text-gray-500 mb-4">
                                        Enter a test route to fetch available couriers, then pick the ones you want. Only selected
                                        couriers will be offered to customers and used for AWB assignment.
                                    </p>

                                    {/* Test Route */}
                                    <div className="flex gap-3 mb-4">
                                        <div className="flex-1">
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Pickup Pincode</label>
                                            <input
                                                type="text"
                                                maxLength={6}
                                                placeholder="e.g. 110001"
                                                value={courierTestPincodes.pickup}
                                                onChange={(e) => setCourierTestPincodes(prev => ({ ...prev, pickup: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Delivery Pincode</label>
                                            <input
                                                type="text"
                                                maxLength={6}
                                                placeholder="e.g. 400001"
                                                value={courierTestPincodes.delivery}
                                                onChange={(e) => setCourierTestPincodes(prev => ({ ...prev, delivery: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                onClick={handleFetchCouriers}
                                                disabled={loadingCouriers}
                                                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 text-sm whitespace-nowrap"
                                            >
                                                {loadingCouriers ? <FaSync className="animate-spin" /> : <FaTruck />}
                                                Fetch Couriers
                                            </button>
                                        </div>
                                    </div>

                                    {/* Selected Couriers Summary */}
                                    {(settings.preferredCouriers || []).length > 0 && (
                                        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                                            <p className="text-sm font-medium text-blue-700 mb-2">
                                                ✓ {settings.preferredCouriers.length} courier(s) selected
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {settings.preferredCouriers.map(c => (
                                                    <span
                                                        key={c.courier_company_id}
                                                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                                                    >
                                                        {c.courier_name}
                                                        <button
                                                            onClick={() => togglePreferredCourier(c.courier_company_id, c.courier_name)}
                                                            className="ml-1 text-blue-400 hover:text-red-500"
                                                        >
                                                            <FaTimes className="text-[10px]" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Courier List */}
                                    {availableCouriers.length > 0 && (
                                        <>
                                            {/* Search & Mode Filter */}
                                            <div className="flex gap-3 mb-3">
                                                <div className="flex-1 relative">
                                                    <input
                                                        type="text"
                                                        placeholder="Search couriers..."
                                                        value={courierSearch}
                                                        onChange={(e) => setCourierSearch(e.target.value)}
                                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                                    />
                                                    <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                    </svg>
                                                </div>
                                                <select
                                                    value={courierModeFilter}
                                                    onChange={(e) => setCourierModeFilter(e.target.value)}
                                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white min-w-[140px]"
                                                >
                                                    <option value="All">All Modes</option>
                                                    <option value="Air">✈️ Air</option>
                                                    <option value="Surface">🚛 Surface</option>
                                                    <option value="Hyperlocal">⚡ Hyperlocal</option>
                                                    <option value="Other">📦 Other</option>
                                                </select>
                                            </div>

                                            {/* Mode counts summary */}
                                            <div className="flex flex-wrap gap-2 mb-3">
                                                {['Air', 'Surface', 'Hyperlocal', 'Other'].map(mode => {
                                                    const count = availableCouriers.filter(c => (c.mode || 'Other') === mode).length;
                                                    if (count === 0) return null;
                                                    const colors = {
                                                        Air: 'bg-sky-100 text-sky-700',
                                                        Surface: 'bg-amber-100 text-amber-700',
                                                        Hyperlocal: 'bg-green-100 text-green-700',
                                                        Other: 'bg-gray-100 text-gray-600',
                                                    };
                                                    return (
                                                        <button
                                                            key={mode}
                                                            onClick={() => setCourierModeFilter(prev => prev === mode ? 'All' : mode)}
                                                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer transition-all ${
                                                                courierModeFilter === mode ? 'ring-2 ring-offset-1 ring-blue-400' : ''
                                                            } ${colors[mode]}`}
                                                        >
                                                            {mode === 'Air' ? '✈️' : mode === 'Surface' ? '🚛' : mode === 'Hyperlocal' ? '⚡' : '📦'} {mode}: {count}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-gray-50 sticky top-0">
                                                        <tr>
                                                            <th className="text-left p-3 font-medium text-gray-600">Select</th>
                                                            <th className="text-left p-3 font-medium text-gray-600">Courier</th>
                                                            <th className="text-left p-3 font-medium text-gray-600">Mode</th>
                                                            <th className="text-left p-3 font-medium text-gray-600">Rate (₹)</th>
                                                            <th className="text-left p-3 font-medium text-gray-600">ETA</th>
                                                            <th className="text-left p-3 font-medium text-gray-600">Rating</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {availableCouriers
                                                            .filter(courier => {
                                                                const nameMatch = !courierSearch || (courier.courier_name || '').toLowerCase().includes(courierSearch.toLowerCase());
                                                                const modeMatch = courierModeFilter === 'All' || (courier.mode || 'Other') === courierModeFilter;
                                                                return nameMatch && modeMatch;
                                                            })
                                                            .map(courier => {
                                                                const isSelected = (settings.preferredCouriers || []).some(
                                                                    c => c.courier_company_id === courier.courier_company_id
                                                                );
                                                                const mode = courier.mode || 'Other';
                                                                const modeBadge = {
                                                                    Air: { bg: 'bg-sky-100 text-sky-700', icon: '✈️' },
                                                                    Surface: { bg: 'bg-amber-100 text-amber-700', icon: '🚛' },
                                                                    Hyperlocal: { bg: 'bg-green-100 text-green-700 font-bold', icon: '⚡' },
                                                                    Other: { bg: 'bg-gray-100 text-gray-600', icon: '📦' },
                                                                }[mode] || { bg: 'bg-gray-100 text-gray-600', icon: '📦' };
                                                                return (
                                                                    <tr
                                                                        key={courier.courier_company_id}
                                                                        className={`border-t cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                                                                        onClick={() => togglePreferredCourier(courier.courier_company_id, courier.courier_name)}
                                                                    >
                                                                        <td className="p-3">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isSelected}
                                                                                readOnly
                                                                                className="w-4 h-4 text-blue-600 rounded"
                                                                            />
                                                                        </td>
                                                                        <td className="p-3 font-medium text-gray-900">{courier.courier_name}</td>
                                                                        <td className="p-3">
                                                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${modeBadge.bg}`}>
                                                                                {modeBadge.icon} {mode}
                                                                            </span>
                                                                        </td>
                                                                        <td className="p-3 text-gray-600">₹{courier.rate || courier.freight_charge || '-'}</td>
                                                                        <td className="p-3 text-gray-600">{courier.etd || courier.estimated_delivery_days || '-'} days</td>
                                                                        <td className="p-3 text-gray-600">{courier.rating || '-'}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        {availableCouriers.filter(courier => {
                                                            const nameMatch = !courierSearch || (courier.courier_name || '').toLowerCase().includes(courierSearch.toLowerCase());
                                                            const modeMatch = courierModeFilter === 'All' || (courier.mode || 'Other') === courierModeFilter;
                                                            return nameMatch && modeMatch;
                                                        }).length === 0 && (
                                                            <tr>
                                                                <td colSpan={6} className="p-4 text-center text-gray-400 text-sm">
                                                                    No couriers match your filters
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Weight-Based Rules */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <div className="flex items-center justify-between mb-2">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                            <FaWeightHanging className="text-orange-500" />
                                            Weight-Based Courier Rules
                                        </h3>
                                        <p className="text-sm text-gray-500">
                                            Define different courier preferences based on package weight. Slabs are evaluated in
                                            order — the first matching slab wins.
                                        </p>
                                    </div>
                                    <button
                                        onClick={addWeightSlab}
                                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                                    >
                                        <FaPlus /> Add Slab
                                    </button>
                                </div>

                                {(settings.courierRules?.weightSlabs || []).length === 0 ? (
                                    <div className="text-center py-8 text-gray-400">
                                        <FaWeightHanging className="text-3xl mx-auto mb-2" />
                                        <p className="text-sm">No weight slabs configured. The global courier mode above will be used for all weights.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4 mt-4">
                                        {(settings.courierRules?.weightSlabs || []).map((slab, idx) => (
                                            <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <div className="flex items-center gap-4 mb-3">
                                                    <div className="flex-1">
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Max Weight (kg)</label>
                                                        <input
                                                            type="number"
                                                            min="0.1"
                                                            step="0.1"
                                                            value={slab.maxWeightKg || 1}
                                                            onChange={(e) => updateWeightSlab(idx, 'maxWeightKg', Number(e.target.value))}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                        />
                                                    </div>
                                                    <div className="flex-1">
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Preference</label>
                                                        <select
                                                            value={slab.preferredMode || "cheapest"}
                                                            onChange={(e) => updateWeightSlab(idx, 'preferredMode', e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                        >
                                                            <option value="cheapest">Cheapest</option>
                                                            <option value="fastest">Fastest</option>
                                                            <option value="custom">Specific Couriers</option>
                                                        </select>
                                                    </div>
                                                    <div className="flex items-end">
                                                        <button
                                                            onClick={() => removeWeightSlab(idx)}
                                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                            title="Remove slab"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </div>

                                                {slab.preferredMode === "custom" && (
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-2">
                                                            Select couriers for this weight slab
                                                            {availableCouriers.length === 0 && " (fetch couriers from the section above first)"}
                                                        </p>
                                                        {availableCouriers.length > 0 ? (
                                                            <div className="flex flex-wrap gap-2">
                                                                {availableCouriers.map(courier => {
                                                                    const isSelected = (slab.preferredCouriers || []).some(
                                                                        c => c.courier_company_id === courier.courier_company_id
                                                                    );
                                                                    return (
                                                                        <button
                                                                            key={courier.courier_company_id}
                                                                            onClick={() => toggleSlabCourier(idx, courier.courier_company_id, courier.courier_name)}
                                                                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all
                                                                                ${isSelected
                                                                                    ? "bg-blue-100 border-blue-400 text-blue-700"
                                                                                    : "bg-white border-gray-300 text-gray-600 hover:border-blue-300"
                                                                                }`}
                                                                        >
                                                                            {isSelected && <FaCheck className="inline mr-1 text-[10px]" />}
                                                                            {courier.courier_name}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-wrap gap-2">
                                                                {(slab.preferredCouriers || []).map(c => (
                                                                    <span key={c.courier_company_id} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                                                        {c.courier_name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ===== PICKUP ADDRESSES TAB (Read-Only) ===== */}
                    {activeTab === "pickup" && (
                        <div className="space-y-6">
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">Shiprocket Pickup Addresses</h3>
                                        <p className="text-sm text-gray-500">
                                            These are your registered pickup locations on Shiprocket. To add, edit, or delete
                                            pickup addresses, visit the{" "}
                                            <a
                                                href="https://app.shiprocket.in/seller/settings/pickup-address"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline inline-flex items-center gap-1"
                                            >
                                                Shiprocket Portal <FaExternalLinkAlt className="text-[10px]" />
                                            </a>
                                            , then refresh here.
                                        </p>
                                    </div>
                                    <button
                                        onClick={fetchPickupAddresses}
                                        className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                                    >
                                        <FaSync className={loadingPickupAddresses ? "animate-spin" : ""} />
                                        Refresh
                                    </button>
                                </div>

                                {/* Pickup Addresses List */}
                                {loadingPickupAddresses ? (
                                    <div className="flex justify-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                    </div>
                                ) : pickupAddresses.length === 0 ? (
                                    <div className="text-center py-8">
                                        <FaMapMarkerAlt className="text-gray-300 text-4xl mx-auto mb-3" />
                                        <p className="text-gray-500">No pickup addresses found on Shiprocket.</p>
                                        <a
                                            href="https://app.shiprocket.in/seller/settings/pickup-address"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-2 inline-flex items-center gap-1 text-blue-600 hover:underline text-sm"
                                        >
                                            Add one on Shiprocket Portal <FaExternalLinkAlt className="text-[10px]" />
                                        </a>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {pickupAddresses.map((addr, index) => (
                                            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                                            <FaMapMarkerAlt className="text-red-500" />
                                                            {addr.pickup_location || `Address ${index + 1}`}
                                                        </h4>
                                                        <p className="text-sm text-gray-600 mt-1">
                                                            {addr.name} • {addr.phone}
                                                        </p>
                                                        <p className="text-sm text-gray-500 mt-1">
                                                            {addr.address}{addr.address_2 ? `, ${addr.address_2}` : ''}, {addr.city}, {addr.state} - {addr.pin_code}
                                                        </p>
                                                    </div>
                                                    <span className={`px-2 py-1 text-xs rounded-full ${addr.status === 1 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                        {addr.status === 1 ? 'Active' : 'Pending'}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ===== BRANCH MAPPING TAB ===== */}
                    {activeTab === "branches" && (
                        <div className="space-y-6">
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Branch → Pickup Address Mapping</h3>
                                <p className="text-sm text-gray-500 mb-6">
                                    Map each branch to a Shiprocket pickup address. When an order from a branch is confirmed with Shiprocket delivery,
                                    the shipment will be created with the mapped pickup address.
                                </p>

                                {branches.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No branches found. Create branches first.
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {branches.map(branch => {
                                            const branchId = branch._id || branch.branchId;
                                            const mapping = settings.branchPickupMappings?.find(
                                                m => m.branchId === branchId
                                            );

                                            return (
                                                <div key={branchId} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                    <div className="flex-1">
                                                        <h4 className="font-medium text-gray-900">{branch.branchName || branch.name}</h4>
                                                        <p className="text-xs text-gray-500">
                                                            {branch.branchLocation?.city || branch.city || ''}, {branch.branchLocation?.state || branch.state || ''}
                                                            {branch.branchPincode ? ` - ${branch.branchPincode}` : ''}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-gray-500">→</span>
                                                        <select
                                                            value={mapping?.pickupLocationName || ""}
                                                            onChange={(e) => {
                                                                const selectedName = e.target.value;
                                                                const selectedAddr = pickupAddresses.find(a => a.pickup_location === selectedName);
                                                                handleBranchMappingChange(
                                                                    branchId,
                                                                    branch.branchName || branch.name,
                                                                    selectedName,
                                                                    selectedAddr?.pin_code || ''
                                                                );
                                                            }}
                                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 min-w-[200px]"
                                                        >
                                                            <option value="">-- Select Pickup Address --</option>
                                                            {pickupAddresses.map((addr, idx) => (
                                                                <option key={idx} value={addr.pickup_location}>
                                                                    {addr.pickup_location} ({addr.city}, {addr.pin_code})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    {mapping?.pickupLocationName ? (
                                                        <FaCheck className="text-green-500" />
                                                    ) : (
                                                        <FaTimes className="text-gray-300" />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {pickupAddresses.length === 0 && (
                                    <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-100">
                                        <p className="text-sm text-yellow-700">
                                            ⚠️ No pickup addresses loaded. Go to the "Pickup Addresses" tab to refresh,
                                            then come back here to map them to branches.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ShiprocketSettings;
